import React from 'react';
import styled from 'styled-components/macro';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useHistory } from 'react-router';

import { State } from '../../reducer';
import { selectors } from '../../auth/accessToken';
import { AuthenticatedAccountUser } from '../../types/management-auth';

import profileActive from './icons/profile-active.svg';
import profileInactive from './icons/profile-inactive.svg';
import profileHover from './icons/profile-hover.svg';
import { colors, distances } from '../../styles/constants';
import { useTranslation } from 'react-i18next';

const mapStateToProps = (state: State) => ({
    accountUser: selectors.getAccountUser(state),
});

const iconMap = {
    profile: {
        active: profileActive,
        inactive: profileInactive,
        hover: profileHover,
    },
};

interface AppBarMerchantLogoProps extends React.DOMAttributes<HTMLSpanElement>, RouteComponentProps<MatchParams> {
    accountUser: AuthenticatedAccountUser;
    icon: keyof typeof iconMap;
}

interface MatchParams {
    accountId: string;
}

const AppBarMerchantLogo = (
    {
        accountUser,
        match,
        icon,
    }: AppBarMerchantLogoProps
) => {
    const { t } = useTranslation();
    const history = useHistory();

    const currentAccount = accountUser.accounts.find((account) => account.account_id === match.params.accountId);
    const iconFromUrl =
        currentAccount && currentAccount.icon_url
            ? {
                active: currentAccount.icon_url,
                inactive: currentAccount.icon_url,
                hover: currentAccount.icon_url,
            }
            : iconMap[icon];

    const getNameToDisplay = () => {
        if (currentAccount) {
            return currentAccount.display_name
                ? currentAccount.display_name
                : currentAccount.business_name
                    ? currentAccount.business_name
                    : currentAccount.account_id;
        }
        return '';
    };

    return (
        <AppBarIconWrapper onClick={() => history.push(`/${currentAccount?.account_id}/accounts`)}>
            <ProfileImageWrapper>
                <ProfileImage icon={iconFromUrl} />
            </ProfileImageWrapper>
            <TextWrapper>
                <div>
                    <span className="logged-in-label">{t('change_account.logged_in')}</span>
                    <StyledName>{getNameToDisplay()}</StyledName>
                    {currentAccount && <StyledCode>{currentAccount?.account_id}</StyledCode>}
                </div>
            </TextWrapper>
        </AppBarIconWrapper>
    );
};

const AppBarIconWrapper = styled.div`
    display: flex;
    padding: ${distances.small};
    margin: ${distances.normal} 0 16px;
    color: ${colors.background};
    cursor: pointer;

    span.logged-in-label {
        margin: 0;
        opacity: 0.7;
        font-size: 13px;
    }

    &:hover,
    &:active {
        color: #fff;
        background: rgba(255, 255, 255, 0.2);
    }

    @media (max-width: 768px) {
        width: 50%;
        margin: 0;
        justify-content: center;
        align-items: center;
    }
`;

interface ProfileImageProps {
    icon: {
        active: string;
        inactive: string;
        hover: string;
    };
}

const ProfileImage = styled.div<ProfileImageProps>`
    width: 32px;
    height: 32px;
    background-image: url(${(props) => props.icon.active});
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;

    &:hover {
        background-image: url(${(props) => props.icon.hover});
    }
`;

const ProfileImageWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin-right: ${distances.small};
    border-radius: 50%;
    background: white;
    overflow: hidden;
    border: 3px solid white;
`;

const TextWrapper = styled.div`
    display: flex;
    flex: 1;
    height: 35px;
    justify-content: space-between;
    align-items: center;
`;

const StyledName = styled.p`
    margin: 0;
`;

const StyledCode = styled.code`
    margin: 0;
    font-size: 10px;
    opacity: 0.7;
    line-height: 1;
    font-style: italic;
`;

export default connect(mapStateToProps)(withRouter(AppBarMerchantLogo));
